import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { getUserFacingErrorMessage } from '../utils/api';
import { UserContext } from '../App';
import NomineeShell from './NomineeShell';
import { Button, Card, Container, Stack, Text } from '../ui';
import NomineeEstateTabs from './NomineeEstateTabs';
import NomineeEstateHeading from './NomineeEstateHeading';

export default function NomineeEstateLegal() {
  const { currentUser } = useContext(UserContext);
  const { ownerSub } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloadErr, setDownloadErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        const resp = await api.get('/nominee/legal-summary', { params: { ownerSub } });
        setData(resp.data || null);
      } catch (e) {
        setError(getUserFacingErrorMessage(e, {
          fallback: 'We could not load the legal summary. Please try again.',
          includeSupportForUnknown: false,
        }));
      } finally {
        setLoading(false);
      }
    })();
  }, [ownerSub]);

  if (!currentUser) return <div>Loading...</div>;
  if (currentUser.type !== 'nominee') {
    navigate('/dashboard');
    return null;
  }

  // Frontend scope (temporary): hide LPA and pre-nuptial from nominee UX as well.
  const hiddenCategories = new Set(['Lasting Power of Attorney', 'Pre-nuptial Agreement']);
  const lockedRaw = Array.isArray(data?.locked) ? data.locked : ['Will'];
  const locked = lockedRaw.filter((x) => !hiddenCategories.has(x));
  const documentsRaw = Array.isArray(data?.documents) ? data.documents : [];
  const documents = documentsRaw.filter((d) => !hiddenCategories.has(d?.typeCategory));

  return (
    <NomineeShell>
      <div style={{ padding: '32px 0' }}>
        <Container>
          <Stack gap={16}>
            <NomineeEstateHeading ownerSub={ownerSub} title="Legal summary" />

            <NomineeEstateTabs ownerSub={ownerSub} active="legal" />

            <div>
              <Button type="button" variant="secondary" onClick={() => navigate('/nominee')}>
                Back to estates
              </Button>
            </div>

            {error && (
              <Card style={{ background: 'var(--peach-50)', borderColor: 'var(--peach-300)' }}>
                <div className="pv-error">{error}</div>
              </Card>
            )}

            {loading ? (
              <Text>Loading…</Text>
            ) : (
              <Stack gap={12}>
                <Card>
                  <Stack gap={10}>
                    <Text as="div" weight={700}>Locked documents</Text>
                    <Text size="sm" muted style={{ margin: 0 }}>
                      Locked documents are only unlocked after admin-verified death.
                    </Text>
                    <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--dusk-700)' }}>
                      {locked.map((x) => <li key={x}>{x}</li>)}
                    </ul>
                    {downloadErr && (
                      <Text size="sm" style={{ margin: 0, color: 'var(--peach-700)' }}>{downloadErr}</Text>
                    )}
                    {locked.includes('Will') && (
                      <div>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={async () => {
                            try {
                              setDownloadErr('');
                              const r = await api.get('/nominee/will/current/download', { params: { ownerSub } });
                              const url = r?.data?.url;
                              if (!url) throw new Error('No URL returned');
                              window.location.assign(url);
                            } catch (e) {
                              setDownloadErr(getUserFacingErrorMessage(e, {
                                fallback: 'We could not download the will. It may not be unlocked yet.',
                                includeSupportForUnknown: false,
                              }));
                            }
                          }}
                        >
                          Download will (if unlocked)
                        </Button>
                      </div>
                    )}
                  </Stack>
                </Card>

                <Card>
                  <Stack gap={12}>
                    <Text as="div" weight={700}>Shared non-sensitive legal documents</Text>
                    {documents.length === 0 ? (
                      <Text muted>No shared legal documents.</Text>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: 'var(--mist-100)' }}>
                              <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid var(--mist-200)' }}>Category</th>
                              <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid var(--mist-200)' }}>Type</th>
                              <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid var(--mist-200)' }}>Institution</th>
                            </tr>
                          </thead>
                          <tbody>
                            {documents.map((r) => (
                              <tr key={r.id}>
                                <td style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>{r.typeCategory || ''}</td>
                                <td style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>{r.type || ''}</td>
                                <td style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>{r.institution || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Stack>
                </Card>
              </Stack>
            )}
          </Stack>
        </Container>
      </div>
    </NomineeShell>
  );
}


